import React from "react"
import clsx from "clsx"
import { isMobileOnly } from "react-device-detect"

import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"
import dateService from "services/date"

import * as BicytrustApi from "features/bike/api/BicytrustApi"
import { ContractRequest, QuoteCancelRequest, QuoteRequest, RequestResponse } from "models"
import { useAlert, useBikeStoreProcess } from "hooks"

import { InsuranceQuote, QuoteFormFields } from "./InsuranceQuote"
import { HelpInfo } from "components/Elements/Help"
import { HelpCircleIcon } from "components/Elements/Icons"
import { CheckCircleIcon } from "components/Elements/Icons"
import { InfosFormFields, InsuranceInfos } from "./InsuranceInfos"
import { ModalDialog } from "components/Elements/ModalDialog"
import { useForm } from "hooks/useForm"
import { formStatusEnum } from "components/Form"

export const enum InsuranceFormSteps {
	UNELIGIBLE = 99,
	QUOTE = "null",
	SIGNED = 2,
	INFOS = 0,
	SENT = 1,
	CANCELED = 9,
}

export const InsuranceForm: React.FC = () => {
	const { t } = useTranslation()
	const { alert } = useAlert()

	const { bikeProcessState } = useBikeStoreProcess()
	const { currentBike: bike } = bikeProcessState
	const { setFormStatus, isLoading, isReady } = useForm({
		defaultFormStatus: formStatusEnum.ready,
	})

	const [currentStep, setCurrentStep] = React.useState(InsuranceFormSteps.UNELIGIBLE)
	const [modalOpen, setModalOpen] = React.useState(false)

	const [isQuoted, setIsQuoted] = React.useState(false)
	const [priceYear, setPriceYear] = React.useState(0)
	const [priceMonth, setPriceMonth] = React.useState(0)

	const [quoteFormFields, setQuoteFormFields] = React.useState<QuoteFormFields>(null)
	const [infoFormFields, setInfoFormFields] = React.useState<InfosFormFields>(null)

	const [existingRequest, setExistingRequest] = React.useState<RequestResponse>(null)

	const fetchRequestStateForBike = React.useCallback(
		() => {
			;(async () => {
				try {
					const { res: req, getError } = await BicytrustApi.requestState(bike.code)
					if (getError()) throw getError()
					switch (req.status) {
						case InsuranceFormSteps.CANCELED:
							setExistingRequest(req)
							setCurrentStep(InsuranceFormSteps.CANCELED)
							break
						case InsuranceFormSteps.SENT:
							setExistingRequest(req)
							setPriceYear(req.prix_aa)
							setPriceMonth(req.prix_rm)
							setCurrentStep(InsuranceFormSteps.SENT)
							break
						case InsuranceFormSteps.INFOS:
							setExistingRequest(req)
							setPriceYear(req.prix_aa)
							setPriceMonth(req.prix_rm)
							setQuoteFormFields({
								warranty_type: req.pack_id,
								warranty_type_name: req.pack_name,
								bike_price: req.bike_price,
								postal_code: req.postal_code,
							} as QuoteFormFields)
							setCurrentStep(InsuranceFormSteps.INFOS)
							break
						case InsuranceFormSteps.SIGNED:
							setExistingRequest(req)
							setPriceYear(req.prix_aa)
							setPriceMonth(req.prix_rm)
							setCurrentStep(InsuranceFormSteps.SIGNED)
							break
						default:
							setCurrentStep(InsuranceFormSteps.QUOTE)
							break
					}
				} catch (error) {
					setCurrentStep(InsuranceFormSteps.UNELIGIBLE)
					//@ts-ignore
					const erroMsg = error?.message?.toString()
					if (erroMsg) alert("error", erroMsg)
					console.error(error)
				}
			})()
		},
		[] // eslint-disable-line
	)

	React.useEffect(() => fetchRequestStateForBike(), [])

	const nextStep = async (formFields: QuoteFormFields) => {
		const quoteRequest: QuoteRequest = {
			request_type: "quote",
			request_data: {
				bike_price: +formFields.bike_price,
				postal_code: formFields.postal_code,
				bike_id: bike.code,
				pack_id: formFields.warranty_type,
			},
		}

		try {
			const { res: prices, getError } = await BicytrustApi.requestQuote(quoteRequest)
			if (getError()) throw getError()
			setPriceYear(prices.prix_aa)
			setPriceMonth(prices.prix_rm)
			setIsQuoted(true)
			setQuoteFormFields(formFields)
			setCurrentStep(InsuranceFormSteps.INFOS)
		} catch (error) {
			//@ts-ignore
			const erroMsg = error?.message?.toString()
			if (erroMsg) alert("error", erroMsg)
			console.error(error)
		}
	}

	const openRecap = (infoFormFields: InfosFormFields) => {
		setInfoFormFields(infoFormFields)
		setModalOpen(true)
	}

	const handleSubmit = async (event: React.SyntheticEvent) => {
		event.preventDefault()
		// prevent form to be submitted again
		setFormStatus(formStatusEnum.loading)

		const contractRequest: ContractRequest = {
			request_type: "contract",
			request_data: {
				bike_price: +quoteFormFields.bike_price,
				postal_code: quoteFormFields.postal_code,
				bike_id: bike.code,
				pack_id: quoteFormFields.warranty_type,
				account_iban: infoFormFields.iban.replace(/\s/g, "").toUpperCase(),
				account_owner: infoFormFields.owner.trim(),
				phone: infoFormFields.phone.trim(),
				first_name: infoFormFields.first_name.trim(),
				last_name: infoFormFields.last_name.trim(),
				contract_start_date: dateService.formatDate(infoFormFields.date, "yyyy-MM-dd"),
				seller_data: {
					user_id: infoFormFields.sellerData.user_id,
					mail: infoFormFields.sellerData.mail,
					first_name: infoFormFields.sellerData.description.prenom,
					last_name: infoFormFields.sellerData.description.nom,
				},
			},
		}
		try {
			const { getError } = await BicytrustApi.requestContract(contractRequest)
			if (getError()) throw getError()
			setModalOpen(false)
			setExistingRequest({
				...existingRequest,
				modify_date: new Date(),
			})
			alert("success", t("insurance.sent_alert", { ns: "bike" }))
			setCurrentStep(InsuranceFormSteps.SENT)
		} catch (error) {
			//@ts-ignore
			const erroMsg = error?.message?.toString()
			if (erroMsg) alert("error", erroMsg)
			console.error(error)
		} finally {
			setFormStatus(formStatusEnum.ready)
		}
	}

	const cancelRequest = async (infoFormFields: InfosFormFields) => {
		const quoteCancelRequest: QuoteCancelRequest = {
			request_type: "quote_cancel",
			request_data: {
				bike_id: bike.code,
				comment: "cancel",
			},
		}
		try {
			const { getError } = await BicytrustApi.requestCancel(quoteCancelRequest)
			if (getError()) throw getError()
			setExistingRequest({
				...existingRequest,
				first_name: infoFormFields.first_name,
				last_name: infoFormFields.last_name,
				phone: infoFormFields.phone,
				status: InsuranceFormSteps.CANCELED.valueOf(),
				modify_date: new Date(),
			})

			setCurrentStep(InsuranceFormSteps.CANCELED)
			setIsQuoted(false)
		} catch (error) {
			//@ts-ignore
			const erroMsg = error?.message?.toString()
			if (erroMsg) alert("error", erroMsg)
			console.error(error)
		}
	}

	if (
		currentStep === InsuranceFormSteps.UNELIGIBLE ||
		(existingRequest === null && currentStep !== InsuranceFormSteps.QUOTE && !isQuoted)
	)
		return <></>

	return (
		<div
			data-cy="form-bike-insurance"
			className={clsx("c-card p-5 md:p-12 mb-5", { "flex flex-col": isMobileOnly })}
		>
			<section className="mb-5">
				<h2 className="c-h2 font-semibold mb-5 uppercase flex items-center">
					{t("insurance.title", { ns: "bike" })}
				</h2>
				<p className="c-p mb-6">{t("insurance.description", { ns: "bike" })}</p>

				<InsuranceQuote
					onSubmit={nextStep}
					locked={isQuoted}
					existingContract={existingRequest}
				/>

				{(currentStep === InsuranceFormSteps.INFOS ||
					currentStep === InsuranceFormSteps.SENT ||
					currentStep === InsuranceFormSteps.SIGNED) && (
					<InsuranceInfos
						disabled={
							currentStep === InsuranceFormSteps.SENT ||
							currentStep === InsuranceFormSteps.SIGNED
						}
						onSubmit={openRecap}
						onCancel={cancelRequest}
						priceYear={priceYear}
						priceMonth={priceMonth}
						existingContract={existingRequest}
					/>
				)}

				{currentStep === InsuranceFormSteps.CANCELED && (
					<div className="flex">
						{t("insurance.date_cancel", { ns: "bike" })}
						{dateService.formatDate(existingRequest.modify_date, "dd/MM/yyyy à HH:mm")}
					</div>
				)}

				{currentStep === InsuranceFormSteps.SENT && existingRequest?.modify_date && (
					<div className="flex">
						<CheckCircleIcon width={28} height={28} />
						<p className="leading-8 ml-2">
							{t("insurance.date_sent", { ns: "bike" })}{" "}
							{dateService.formatDate(
								existingRequest.modify_date,
								"dd/MM/yyyy à HH:mm"
							)}
						</p>
					</div>
				)}

				{currentStep === InsuranceFormSteps.SIGNED && existingRequest?.modify_date && (
					<div className="flex">
						<CheckCircleIcon width={28} height={28} />
						<p className="leading-8 ml-2">
							{t("insurance.date_signed", { ns: "bike" })}{" "}
							{dateService.formatDate(
								existingRequest.modify_date,
								"dd/MM/yyyy à HH:mm"
							)}
						</p>
					</div>
				)}

				<ModalDialog
					isOpen={modalOpen}
					closeModal={() => setModalOpen(false)}
					className={"max-w-3xl"}
					title={t("insurance.modal_title", { ns: "bike" })}
				>
					<div className="p-12 pt-8">
						<div className="flex mb-8">
							<div className="w-1/2">
								{t("insurance.selected_bike", { ns: "bike" })}
								<div className="font-bold font-[Jost]">{bike?.code}</div>
							</div>
							<div className="w-1/2">
								{t("insurance.warranty_type", { ns: "bike" })} :{" "}
								<div className="font-bold font-[Jost]">
									{quoteFormFields?.warranty_type_name}
								</div>
								<div className="font-bold font-[Jost]">
									{t("insurance.modal_price", { ns: "bike" })} {priceYear} €
								</div>
							</div>
						</div>
						<div className="mb-8">{t("insurance.modal_info", { ns: "bike" })}</div>
						{isReady && (
							<div className="pb-8">
								<button
									data-cy={"insurance-cancel"}
									type="button"
									className="c-btn c-btn--primary uppercase c-btn--m px-7 float-left"
									onClick={() => setModalOpen(false)}
								>
									{t("global.cancel")}
								</button>

								<button
									data-cy={"insurance-confirm"}
									type="button"
									className="c-btn c-btn--primary uppercase c-btn--m px-7 float-right"
									onClick={handleSubmit}
								>
									{t("global.confirm")}
								</button>
							</div>
						)}
						{isLoading && (
							<div className="flex justify-center">
								<div data-cy="spinner" className="c-spinner"></div>
							</div>
						)}
					</div>
				</ModalDialog>

				<HelpInfo>
					<HelpCircleIcon size={28} />
					<p className="ml-4 mt-1">{t("insurance.help", { ns: "bike" })}</p>{" "}
					<NavLink
						data-cy="insurance-help"
						target="_blank"
						to={
							"https://bicytrust.notion.site/0ea2f6426df54ff480ddb176f2fd8aee?v=ec23c9de0cad482aabac7edb95d3949d"
						}
						className="ml-2 mt-1 underline"
					>
						{t("insurance.help_link", { ns: "bike" })}
					</NavLink>
				</HelpInfo>
			</section>
		</div>
	)
}
